import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api";
import AppLayout from "../components/AppLayout";
import { Empty, ErrorNote, Loading } from "../components/Feedback";
import { CHANGED_EVENT } from "../components/NotificationBell";
import { Btn, Card } from "../components/Shared";

// /notifications — what the bell counts: builds, imports, migrations, splits and merges, the
// data check (and checksums, quotas, releases once those land), newest first, and the person's
// choice per kind: none, in the portal, or in the portal and by e-mail.
//
//   GET  /api/me/notifications?limit=100         {items, unread}
//   POST /api/me/notifications/{id}/read         one read
//   POST /api/me/notifications/read-all          all read
//   GET  /api/me/notification-preferences        {email, email_available, kinds: [{kind, label, description, channel,
//                                                channels}]}: only `channels` are offered (announcements
//                                                cannot be switched off)
//   PUT  /api/me/notification-preferences        {channels: {kind: channel}}

const CHANNELS = [
  { value: "none", label: "Off" },
  { value: "portal", label: "In the portal" },
  { value: "email", label: "In the portal and by e-mail" },
];

function when(iso) {
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

function changed() {
  window.dispatchEvent(new Event(CHANGED_EVENT));
}

function NotificationItem({ n, onRead }) {
  const unread = !n.read_at;
  return (
    <li style={{ padding: "12px 0", borderBottom: "1px solid #e5e7eb", display: "flex", gap: 12, alignItems: "flex-start" }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: unread ? 700 : 500, color: "#1a1a1a" }}>
          {unread && <span style={{ color: "var(--dp-accent-dark)" }}>New: </span>}
          {n.link ? <Link data-tap to={n.link} style={{ color: "inherit" }}>{n.title}</Link> : n.title}
        </div>
        {n.body && <div style={{ fontSize: 13, color: "#374151", marginTop: 2, whiteSpace: "pre-wrap" }}>{n.body}</div>}
        <div style={{ fontSize: 12, color: "#4b5563", marginTop: 4 }}>
          {when(n.created_at)}
          {n.email_status === "sent" && " · e-mailed"}
          {n.email_status === "pending" && " · e-mail waiting to be sent"}
          {n.email_status === "failed" && " · the e-mail could not be sent"}
        </div>
      </div>
      {unread && (
        <Btn variant="secondary" onClick={() => onRead(n)}>
          Mark as read<span style={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0,0,0,0)" }}>: {n.title}</span>
        </Btn>
      )}
    </li>
  );
}

function Preferences() {
  const [prefs, setPrefs] = useState(null);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(null);

  useEffect(() => {
    let live = true;
    api.get("/me/notification-preferences")
      .then(p => { if (live) setPrefs(p); })
      .catch(e => { if (live) setError(e); });
    return () => { live = false; };
  }, []);

  if (error && !prefs) return <ErrorNote error={error} />;
  if (!prefs) return <Loading />;

  const choose = async (kind, channel) => {
    setError(null);
    setSaved(null);
    try {
      const p = await api.put("/me/notification-preferences", { channels: { [kind]: channel } });
      setPrefs(p);
      setSaved(`Saved: ${p.kinds.find(k => k.kind === kind)?.label}.`);
    } catch (e) {
      setError(e);
    }
  };

  return (
    <Card style={{ marginTop: 20 }}>
      <h2 style={{ margin: "0 0 6px", fontSize: 18, fontWeight: 700 }}>Settings</h2>
      <p style={{ margin: "0 0 12px", fontSize: 13, color: "#4b5563" }}>
        For each kind, choose whether you hear about it at all, only here, or also by e-mail
        {prefs.email ? ` to ${prefs.email}` : ""}.
        {!prefs.email && " Your account has no e-mail address, so nothing can be e-mailed."}
        {prefs.email && !prefs.email_available &&
          " E-mail is not connected in this portal yet: e-mails wait until an administrator connects it."}
      </p>
      <ErrorNote error={error} />
      {saved && <p role="status" style={{ fontSize: 13, color: "var(--dp-accent-dark)", margin: "0 0 10px" }}>{saved}</p>}
      {prefs.kinds.map(k => (
        <fieldset key={k.kind} style={{ border: "none", borderTop: "1px solid #e5e7eb", margin: 0, padding: "10px 0" }}>
          <legend style={{ fontSize: 14, fontWeight: 600, padding: 0, float: "left", width: "100%" }}>{k.label}</legend>
          <div style={{ clear: "both", fontSize: 12, color: "#4b5563", marginBottom: 6 }}>{k.description}</div>
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
            {CHANNELS.filter(c => !k.channels || k.channels.includes(c.value)).map(c => (
              <label key={c.value} style={{ display: "inline-flex", gap: 6, alignItems: "center", fontSize: 13 }}>
                <input type="radio" name={`channel-${k.kind}`} value={c.value} checked={k.channel === c.value}
                       onChange={() => choose(k.kind, c.value)} />
                {c.label}
              </label>
            ))}
          </div>
        </fieldset>
      ))}
    </Card>
  );
}

export default function NotificationsPage() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    api.get("/me/notifications?limit=100")
      .then(d => { setData(d); setError(null); })
      .catch(setError);
  }, []);

  useEffect(() => { load(); }, [load]);

  const readOne = async n => {
    try {
      await api.post(`/me/notifications/${n.id}/read`);
      changed();
      load();
    } catch (e) { setError(e); }
  };

  const readAll = async () => {
    try {
      await api.post("/me/notifications/read-all");
      changed();
      load();
    } catch (e) { setError(e); }
  };

  return (
    <AppLayout title="Notifications">
      <Card>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 8, flexWrap: "wrap" }}>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>
            {data ? (data.unread ? `${data.unread} unread` : "All read") : "Your notifications"}
          </h2>
          {data?.unread > 0 && <Btn variant="secondary" onClick={readAll}>Mark all as read</Btn>}
        </div>
        <ErrorNote error={error} />
        {!data && !error && <Loading />}
        {data && data.items.length === 0 && (
          <Empty>Nothing yet. Builds you start, your imports, migrations of your projects and data
            check errors in your tables will show here.</Empty>
        )}
        {data && data.items.length > 0 && (
          <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
            {data.items.map(n => <NotificationItem key={n.id} n={n} onRead={readOne} />)}
          </ul>
        )}
      </Card>
      <Preferences />
    </AppLayout>
  );
}
